import { csrfHeaders } from './dcms-csrf.js';

/**
 * Class implementing row selection functionality for tables.
 */
export class Selectable {
    /**
     * Selectable class constructor.
     * Initializes the table list and the per-table state (WeakMap keyed by the table element:
     * a table removed from the page, e.g. the old list after an AJAX update, drops its entries
     * by itself).
     */
    constructor() {
        this.tables = [];
        this.selectAllIcons = new WeakMap();
        this.controlRows = new WeakMap();
    }

    /**
     * Initializes the selectable functionality for all tables with the matching attribute.
     * Finds tables with the data-dcms-selectable="true" attribute and adds selection functionality to them.
     * Called again after every AJAX update of a Recordset list (list_head.tpl): the state is
     * not reset, because the tables outside the replaced container are skipped below and
     * would lose it (clicking their checkboxes threw a TypeError, the action bar did not show).
     */
    init() {
        this.tables = document.querySelectorAll('table[data-dcms-selectable="true"]');
        DCMSDebugLog(`Found ${this.tables.length} tables with selectable attribute`, 3, 'Selectable');

        this.tables.forEach((table, index) => {
            // Skip tables that already have select columns (avoid double-init)
            if (table.querySelector('.dcms-table-select')) {
                DCMSDebugLog(`Table ${index + 1} already has select column, skipping`, 3, 'Selectable');
                return;
            }
            DCMSDebugLog(`Initializing table ${index + 1}`, 3, 'Selectable');
            this._addSelectColumn(table);
        });
    }

    /**
     * Adds a row-selection column to the table.
     * @param {HTMLTableElement} table - The table the column is added to.
     * @private
     */
    _addSelectColumn(table) {
        DCMSDebugLog('Adding select column to table', 3, 'Selectable');
        const headerRow = table.querySelector('thead tr');
        if (headerRow) {
            const headerCell = document.createElement('th');
            headerCell.className = 'dcms-table-select w-10 !py-0 !pr-0 !pl-3';
            headerCell.setAttribute('scope', 'col');
            const selectAllIcon = this._createIcon('check_box_outline_blank', __i18n("selectAll"));
            selectAllIcon.classList.add('cursor-pointer');
            selectAllIcon.addEventListener('click', () => this._toggleSelectAll(table));
            headerCell.appendChild(selectAllIcon);
            
            // The select column is always inserted as the first column
            headerRow.insertBefore(headerCell, headerRow.firstElementChild);

            this.selectAllIcons.set(table, selectAllIcon);
            DCMSDebugLog('Added header cell for select column', 3, 'Selectable');
        } else {
            DCMSDebugLog('Could not find header row', 1, 'Selectable');
        }

		const bodyRows = table.querySelectorAll('tbody tr');
		    bodyRows.forEach((row, index) => {
		        const cell = document.createElement('td');
		        cell.className = 'dcms-table-select w-10 !py-0 !pr-0 !pl-3';
		        if (row.getAttribute('data-dcms-selectable-row') !== 'no') {
		            const checkboxIcon = this._createIcon('check_box_outline_blank', __i18n("selectRow"));
		            checkboxIcon.classList.add('cursor-pointer');
		            checkboxIcon.addEventListener('click', (e) => this._toggleSelectRow(e, row));
		            cell.appendChild(checkboxIcon);
		        } else {
		            cell.classList.add('dcms-table-select-disabled');
		        }
		        
		        // The select cell is always inserted as the first column
		        row.insertBefore(cell, row.firstElementChild);
		        
		        DCMSDebugLog(`Added select cell to row ${index + 1}`, 3, 'Selectable');
		    });

        // Copy border styles from the first data column (nth-child(2) after inserting the select column)
        const firstDataColumn = table.querySelector('thead tr th:nth-child(2)');
        if (firstDataColumn) {
            const borderStyles = window.getComputedStyle(firstDataColumn);
            const borderProperties = ['borderTop', 'borderRight', 'borderBottom', 'borderLeft'];
            
            table.querySelectorAll('.dcms-table-select').forEach(cell => {
                borderProperties.forEach(prop => {
                    cell.style[prop] = borderStyles[prop];
                });
            });
        }
    }    
    
    /**
     * Creates a Material Symbols Rounded icon.
     * @param {string} iconName - Icon name.
     * @param {string} title - Icon title (tooltip).
     * @returns {HTMLSpanElement} - The created icon.
     * @private
     */
    _createIcon(iconName, title) {
        const icon = document.createElement('span');
        icon.classList.add('msr', 'flex', 'items-center', 'justify-center', 'w-full', 'h-full', 'text-gray-400', 'dark:text-neutral-500');
        icon.textContent = iconName;
        icon.title = title;
        return icon;
    }

 	/**
     * Toggles the selection of all rows in the table.
     * @param {HTMLTableElement} table - The table for which we toggle the selection.
     * @private
     */
    _toggleSelectAll(table) {
	    DCMSDebugLog('Toggling select all', 3, 'Selectable');
	    const selectAllIcon = this.selectAllIcons.get(table);
	    const isSelected = selectAllIcon.textContent === 'check_box';
	    const newIconName = isSelected ? 'check_box_outline_blank' : 'check_box';
	    selectAllIcon.textContent = newIconName;
	
	    table.querySelectorAll('tbody tr').forEach(row => {
	        if (row.getAttribute('data-dcms-selectable-row') !== 'no') {
	            const checkboxIcon = row.querySelector('td.dcms-table-select .msr');
	            if (checkboxIcon) {
	                checkboxIcon.textContent = newIconName;
	                row.classList.toggle('selected', !isSelected);
	            }
	        }
	    });
	
	    this._updateControlRow(table);
	 }


	/**
     * Sets whether a given row can be selected.
     * @param {HTMLTableRowElement} row - The row for which we set the selectable state.
     * @param {boolean} selectable - Whether the row should be selectable.
     */
    setRowSelectable(row, selectable) {
        if (selectable) {
            row.removeAttribute('data-dcms-selectable-row');
        } else {
            row.setAttribute('data-dcms-selectable-row', 'no');
        }
        // You can add logic here to update the row's appearance
    }

    /**
     * Toggles the selection of a single row.
     * @param {Event} e - Click event.
     * @param {HTMLTableRowElement} row - The row whose selection we toggle.
     * @private
     */
    _toggleSelectRow(e, row) {
        DCMSDebugLog('Toggling select row', 3, 'Selectable');
        const checkboxIcon = e.target;
        const isSelected = checkboxIcon.textContent === 'check_box';
        checkboxIcon.textContent = isSelected ? 'check_box_outline_blank' : 'check_box';
        row.classList.toggle('selected', !isSelected);

        const table = row.closest('table');
        this._updateSelectAllIcon(table);
        this._updateControlRow(table);
    }

    /**
     * Updates the "select all" icon based on the rows' selection state.
     * @param {HTMLTableElement} table - The table for which we update the icon.
     * @private
     */
    _updateSelectAllIcon(table) {
        DCMSDebugLog('Updating select all icon', 3, 'Selectable');
        const selectAllIcon = this.selectAllIcons.get(table);
        const allRows = table.querySelectorAll('tbody tr');
        const selectedRows = table.querySelectorAll('tbody tr.selected');

        if (selectedRows.length === allRows.length) {
            selectAllIcon.textContent = 'check_box';
        } else if (selectedRows.length === 0) {
            selectAllIcon.textContent = 'check_box_outline_blank';
        } else {
            selectAllIcon.textContent = 'indeterminate_check_box';
        }
    }
    
	 /**
     * Gets the existing control row or creates a new one.
     * @param {HTMLTableElement} table - The table for which we get/create the control row.
     * @returns {HTMLTableRowElement} - The control row.
     * @private
     */
    _getOrCreateControlRow(table) {
        let controlRow = this.controlRows.get(table);
        if (!controlRow) {
            controlRow = table.querySelector('.dcms-control-row');
            if (!controlRow) {
                controlRow = document.createElement('tr');
                controlRow.className = 'dcms-control-row';
                const cell = document.createElement('th');
                cell.colSpan = table.querySelector('thead tr').children.length;
                cell.className = 'p-2';
                // Copy styles from the first header row
                const firstHeaderRow = table.querySelector('thead tr:first-child');
                if (firstHeaderRow) {
                    const styles = window.getComputedStyle(firstHeaderRow);
                    cell.style.cssText = styles.cssText;
                }
                controlRow.appendChild(cell);
                const thead = table.querySelector('thead');
                thead.appendChild(controlRow);
            }
            this.controlRows.set(table, controlRow);
        }
        return controlRow;
    }
	
  	/**
     * Creates the cell for the selectable controls in the control row.
     * @param {HTMLTableRowElement} controlRow - The control row.
     * @returns {HTMLElement} - The cell for the selectable controls.
     * @private
     */
    _createSelectableCell(controlRow) {
        const cell = controlRow.querySelector('th');
        if (!cell) {
            DCMSDebugLog('Could not find th element in control row', 1, 'Selectable');
            return null;
        }
        let selectableCell = cell.querySelector('.dcms-selectable-controls');
        if (!selectableCell) {
            selectableCell = document.createElement('div');
            selectableCell.className = 'dcms-selectable-controls';
            cell.appendChild(selectableCell);
        }
        return selectableCell;
    }

    /**
     * Updates the control row based on the number of selected rows.
     * @param {HTMLTableElement} table - The table for which we update the control row.
     * @private
     */
    _updateControlRow(table) {
        const selectedRows = table.querySelectorAll('tbody tr.selected');
        let controlRow = this.controlRows.get(table);

        if (selectedRows.length > 0) {
            if (!controlRow) {
                controlRow = this._getOrCreateControlRow(table);
            }
            const selectableCell = controlRow.querySelector('.dcms-selectable-controls') || this._createSelectableCell(controlRow);
            if (selectableCell) {
                this._updateSelectableContent(selectableCell, selectedRows.length, table);
            }
        } else if (controlRow) {
            const selectableCell = controlRow.querySelector('.dcms-selectable-controls');
            if (selectableCell) {
                selectableCell.remove();
            }
            if (controlRow.querySelector('th').children.length === 0) {
                controlRow.remove();
                this.controlRows.delete(table);
            }
        }
    }


 	 /**
     * Updates the content of the selectable controls.
     * @param {HTMLElement} selectableCell - The cell for the selectable controls.
     * @param {number} selectedCount - Number of selected rows.
     * @param {HTMLTableElement} table - The table the control row belongs to.
     * @private
     */
    _updateSelectableContent(selectableCell, selectedCount, table) {
        const actionButtons = [];

        const selectedText = __i18n("selectedRowAction").replace('{%s}', selectedCount);

        if (table.dataset.dcmsSelectableEditUrl) {
            actionButtons.push(this._createActionButton('edit', 'green', 'editSelected', table.dataset.dcmsSelectableEditUrl, table.id));
        }
        if (table.dataset.dcmsSelectableDeleteUrl) {
            actionButtons.push(this._createActionButton('delete', 'red', 'deleteSelected', table.dataset.dcmsSelectableDeleteUrl, table.id));
        }
        if (table.dataset.dcmsSelectableExportUrl) {
            actionButtons.push(this._createActionButton('export_notes', 'sky', 'exportSelected', table.dataset.dcmsSelectableExportUrl, table.id));
        }

        selectableCell.innerHTML = `
            <div class="flex items-center space-x-2">
                <span>${selectedText}</span>
                ${actionButtons.map(button => button.html).join('')}
            </div>
        `;

        // Add event handling for the buttons
        actionButtons.forEach(button => {
            const buttonElement = selectableCell.querySelector(`#${button.id}`);
            if (buttonElement) {
                buttonElement.addEventListener('click', () => this._handleActionButtonClick(button.action, button.url, table));
            }
        });
    }

    /**
     * Creates an action button.
     * @param {string} action - Action name (edit, delete, export_notes).
     * @param {string} color - Button background color.
     * @param {string} textKey - Translation key for the button text.
     * @param {string} url - Action URL.
     * @param {string} tableId - Table ID.
     * @returns {Object} - Object containing the action button's id, html and url.
     * @private
     */
    _createActionButton(action, color, textKey, url, tableId) {
        const buttonId = `dcmsSelectable_${action}Button_${tableId}`;
        return {
            id: buttonId,
            action: action,
            html: `
                <button id="${buttonId}" class="btn size-2 bg-light-${color} icon">
                    <i class="msr">${action}</i> ${__i18n(textKey)}
                </button>
            `,
            url: url
        };
    }

    /**
     * Handles a click on an action button.
     * @param {string} action - Action name (delete, edit, export_notes).
     * @param {string} url - Action URL.
     * @param {HTMLTableElement} table - The table the action button belongs to.
     * @private
     */
    _handleActionButtonClick(action, url, table) {
        const selectedIds = Array.from(table.querySelectorAll('tbody tr.selected'))
            .map(row => row.dataset.rowId);

        // DELETE: DCMSPopup confirmation + AJAX
        if (action === 'delete') {
            this._handleMultipleDelete(url, selectedIds, table);
            return;
        }

        // Other actions: modal iframe (edit, export). The selected ids go as a JSON array in
        // datasetSelectedRowsIds — read by multipleEditHandler, sent by the multiple delete
        // too — and as the comma-separated `ids` list kept for custom editUrl / exportUrl
        // endpoints (ambiguous for a composite key: its encoded id contains commas).
        // datasetPopup=1: the page is the content of a modal, so a dataset with page
        // navigation closes the modal after the save instead of loading the list into it.
        const fullUrl = new URL(url, window.location.href);
        fullUrl.searchParams.set('datasetSelectedRowsIds', JSON.stringify(selectedIds));
        fullUrl.searchParams.set('ids', selectedIds.join(','));
        fullUrl.searchParams.set('datasetPopup', '1');

        // After the modal closes: afterEditCallback when given (on every close). The results
        // table of a dataset list (views/Recordset/list_head.tpl) is not reloaded — its
        // listener refreshes it on the dcms:recordset:refresh message of the save (a list
        // with page navigation in place, keeping its page; a modal-mode list once), so
        // closing without a save leaves it as it is. Any other table: page reload, as before.
        const isDatasetList = !!table.closest('.ajax-list-container')
            && typeof window.dcmsListRefresh === 'function';

        APP.modalsInstance.showFullScreenPopup(null, __i18n("selectedRowsModalTitle"), {
            onClosed: () => {
                const afterEditCallback = table.dataset.dcmsSelectableAfterEdit;
                if (afterEditCallback && typeof window[afterEditCallback] === 'function') {
                    window[afterEditCallback]();
                } else if (!isDatasetList) {
                    window.location.reload();
                }
            }
        }, false, fullUrl.toString());
    }

    /**
     * Handles deletion of multiple records — DCMSPopup + AJAX fetch
     * @param {string} url - multipleDeleteHandler URL
     * @param {Array<string>} selectedIds - List of selected IDs
     * @param {HTMLTableElement} table - The table
     * @private
     */
    _handleMultipleDelete(url, selectedIds, table) {
        // Get DCMSPopup from the top window
        let DCMSPopup;
        try {
            let w = window;
            while (w !== w.top) w = w.parent;
            DCMSPopup = w.DCMSPopup;
        } catch(e) { DCMSPopup = window.DCMSPopup; }

        const count = selectedIds.length;
        const idsJson = JSON.stringify(selectedIds);

        // Get i18n — from window (set by list_head.tpl) or fallback
        const i18n = window._dcmsDeleteMultipleI18n || {
            confirmTitle: 'Delete confirmation',
            confirmBody: `Are you sure you want to delete ${count} records?`,
            delete: 'Delete',
            cancel: 'Cancel',
        };

        const confirmBody = (i18n.confirmBody || '').replace('%d', count).replace('%s', count);

        if (!DCMSPopup) {
            // Fallback: a simple confirm + form
            if (confirm(confirmBody)) {
                this._executeMultipleDelete(url, idsJson, table);
            }
            return;
        }

        const self = this;
        const popup = new DCMSPopup({
            title: i18n.confirmTitle,
            body: '<p style="margin:0;font-size:14px;line-height:1.6">' + confirmBody + '</p>',
            buttons: [
                {
                    label: i18n.cancel,
                    style: 'secondary',
                    onClick: function(p) { p.close(); }
                },
                {
                    label: i18n.delete + ' (' + count + ')',
                    style: 'danger',
                    onClick: function(p) {
                        p.close();
                        self._executeMultipleDelete(url, idsJson, table);
                    }
                }
            ]
        });
        popup.open();
    }

    /**
     * Executes the multiple delete via AJAX
     * @param {string} url - multipleDeleteHandler URL
     * @param {string} idsJson - JSON string with IDs
     * @param {HTMLTableElement} table - The table
     * @private
     */
    _executeMultipleDelete(url, idsJson, table) {
        const fullUrl = new URL(url, window.location.origin);

        const body = new URLSearchParams();
        body.set('multipleDeleteConfirm', '1');
        body.set('datasetSelectedRowsIds', idsJson);

        fetch(fullUrl.toString(), {
            method: 'POST',
            headers: csrfHeaders({
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json',
            }),
            body: body
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                if (typeof window.dcmsDeleteNotifySuccess === 'function') {
                    // Broadcast refresh + toast — on a modal-mode list the toast comes from the
                    // list listener, so the list (list_head.tpl) decides
                    window.dcmsDeleteNotifySuccess('multipleDelete', data.message || 'Deleted');
                } else {
                    // Broadcast refresh
                    if (typeof window.dcmsDeleteBroadcastRefresh === 'function') {
                        window.dcmsDeleteBroadcastRefresh('multipleDelete', data.message || '');
                    }
                    // Toast
                    if (typeof window.dcmsDeleteShowToast === 'function') {
                        window.dcmsDeleteShowToast(data.message || 'Deleted', true);
                    }
                }
            } else {
                if (typeof window.dcmsDeleteShowToast === 'function') {
                    window.dcmsDeleteShowToast(data.message || 'Delete failed', false);
                } else {
                    alert(data.message || 'Delete failed');
                }
            }
        })
        .catch(err => {
            if (typeof window.dcmsDeleteShowToast === 'function') {
                window.dcmsDeleteShowToast('Delete failed', false);
            } else {
                alert('Delete failed');
            }
        });
    }
}